"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";

type ToastTone = "info" | "success" | "error" | "warning";

interface ToastInput {
  title?: string;
  message: string;
  tone?: ToastTone;
  durationMs?: number;
}

interface ToastItem {
  id: number;
  createdAt: number;
  title?: string;
  message: string;
  tone: ToastTone;
  durationMs: number;
}

interface ToastContextValue {
  push(input: ToastInput): void;
  success(message: string, title?: string): void;
  error(message: string, title?: string): void;
  warning(message: string, title?: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
let counter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    ({ title, message, tone = "info", durationMs = 3500 }: ToastInput) => {
      if (!message) {
        return;
      }
      const id = ++counter;
      const toast: ToastItem = {
        id,
        title,
        message,
        tone,
        durationMs,
        createdAt: Date.now(),
      };
      setToasts((prev) => [...prev, toast]);
      window.setTimeout(() => remove(id), durationMs);
    },
    [remove],
  );

  const api = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (message, title) => push({ message, title, tone: "success" }),
      error: (message, title) => push({ message, title, tone: "error" }),
      warning: (message, title) => push({ message, title, tone: "warning" }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-0 z-[1000] flex flex-col items-end gap-2 px-4 py-6 sm:px-6">
          {toasts.map((toast) => (
            <button
              key={toast.id}
              type="button"
              onClick={() => remove(toast.id)}
              className={getToastClass(toast.tone)}
            >
              {toast.title && <div className="text-sm font-semibold">{toast.title}</div>}
              <div className="text-sm">{toast.message}</div>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function getToastClass(tone: ToastTone) {
  const base = "pointer-events-auto w-full max-w-sm rounded-2xl border px-4 py-3 text-left shadow transition";
  switch (tone) {
    case "success":
      return `${base} border-emerald-300 bg-emerald-50 text-emerald-900`;
    case "error":
      return `${base} border-rose-300 bg-rose-50 text-rose-900`;
    case "warning":
      return `${base} border-amber-300 bg-amber-50 text-amber-900`;
    default:
      return `${base} border-slate-200 bg-white text-slate-900`;
  }
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside ToastProvider");
  }
  return context;
}
